import type { MouseEvent } from 'react';
import { useState, useMemo, useEffect } from 'react';

import type { Technology, TechnologyOption } from '../../../../domain/technology';
import { SELECTED_TECH_CATEGORIES } from '../../../../domain/user';
import { useUserSettings } from '../../../../features/user/useUserSettings';
import { TechnologiesService } from '../../../../services/technologies';
import { useDispatchTrackEventWithLocation } from '../../../../services/tracking/hooks/useDispatchTrackEventWithLocation';

export const useTechCategoriesFilter = ({
  technologies,
  appliedCategories,
}: {
  technologies: Technology[] | undefined;
  appliedCategories: string[];
}) => {
  const { setUserSettings } = useUserSettings();
  const [isOpened, setIsOpened] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const [searchText, setSearchText] = useState('');
  const searchWords = searchText.toLowerCase().split(' ');

  const dispatchTrackEventWithLocation = useDispatchTrackEventWithLocation();

  const handleTracking = (action: string, eventName: string) => {
    dispatchTrackEventWithLocation('technographics', action, `tech categories/${eventName}`);
  };

  useEffect(() => {
    setSelectedCategories(appliedCategories);
  }, [appliedCategories]);

  const options = useMemo(
    () => (technologies ? TechnologiesService.convertTechnologiesToOptions(technologies) : []),
    [technologies]
  );

  const filteredOptions = options.filter(({ name }) => searchWords.every((word) => name.toLowerCase().includes(word)));

  const saveSelectedCategoriesToStorage = (selectedCategories: string[]) => {
    setUserSettings({ [SELECTED_TECH_CATEGORIES]: selectedCategories });
  };

  const handleOpenDropdown = () => {
    handleTracking('see tech categories', appliedCategories.length.toString());
    setIsOpened(true);
  };

  const handleClickOnIcon = (event: MouseEvent<HTMLDivElement>): void => {
    if (selectedCategories.length === 0) {
      return;
    }

    event.stopPropagation();
    setSelectedCategories([]);
    saveSelectedCategoriesToStorage([]);
  };

  const handleTechCategoriesSelect = (option: TechnologyOption) => {
    const { name, items, isChild } = option;
    if (isChild) {
      setSelectedCategories((prev) => {
        if (prev.includes(name)) {
          return prev.filter((item) => item !== name);
        }
        return [...prev, name];
      });
    } else {
      setSelectedCategories((prev) => {
        const isChecked = items.every((item) => selectedCategories.includes(item));
        if (isChecked) {
          return prev.filter((item) => !items.includes(item));
        }
        return [...prev, ...items];
      });
    }
  };

  const handleApplySelectedTechCategories = () => {
    handleTracking('apply tech categories', `${selectedCategories.length}/${selectedCategories.join(',')}`);
    saveSelectedCategoriesToStorage(selectedCategories);
    setIsOpened(false);
  };

  return {
    isOpened,
    handleOpenDropdown,
    handleCloseDropdown: () => setIsOpened(false),
    selectedCategories,
    searchText,
    handleSearchText: setSearchText,
    filteredOptions,
    handleClickOnIcon,
    handleTechCategoriesSelect,
    handleApplySelectedTechCategories,
  };
};
